import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/client";
import { takePendingState, statesMatch } from "@/lib/appleStateStore";
import { toast } from "@/hooks/use-toast";
import * as analytics from "@/lib/analytics";

/**
 * Listens for deep link events on native platforms (iOS/Android)
 * and navigates to the corresponding in-app route.
 *
 * Also completes browser-based OAuth round trips: if the callback URL carries
 * Supabase tokens (hash) or a PKCE code (query), establish the session here so
 * the user lands back inside the app instead of being stranded in a browser.
 */
export const useDeepLinks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = CapApp.addListener("appUrlOpen", async ({ url }) => {
      try {
        // Apple's Android sign-in returns through ventapp://auth-callback with
        // the tokens and the state value we sent. Match the state against the
        // one stored on device before creating any session, then route without
        // leaving the callback URL (which carries tokens) in navigation state.
        if (url.startsWith('ventapp://')) {
          if (!url.includes('auth-callback')) return;

          try {
            const { Browser } = await import('@capacitor/browser');
            await Browser.close();
          } catch {
            /* tab already closed */
          }

          const cb = new URL(url.replace('ventapp://', 'https://ventapp.local/'));
          const params = new URLSearchParams(
            cb.search ? cb.search.replace(/^\?/, '') : cb.hash.replace(/^#/, '')
          );
          const expected = await takePendingState();
          const returnedState = params.get('state');

          const fail = (reason: 'canceled' | 'provider_error' | 'exception') => {
            analytics.track('OAuth Failed', { auth_method: 'apple', reason });
            if (reason === 'canceled') {
              toast({ title: 'Sign in was canceled.' });
            } else {
              toast({
                variant: 'destructive',
                title: 'Sign in failed',
                description: "We couldn't complete Apple sign in. Please try again.",
              });
            }
            navigate('/auth', { replace: true });
          };

          if (!expected || !returnedState || !statesMatch(expected, returnedState)) {
            // State missing, expired or not matching the pending request.
            fail('exception');
            return;
          }

          const idToken = params.get('id_token');
          if (params.get('success') !== 'true' || !idToken) {
            fail(params.get('reason') === 'canceled' ? 'canceled' : 'provider_error');
            return;
          }

          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: idToken,
          });
          if (error) {
            fail('provider_error');
            return;
          }

          analytics.track('OAuth Completed', { auth_method: 'apple' });
          navigate('/app', { replace: true });
          return;
        }

        const parsedUrl = new URL(url);
        const hashParams = new URLSearchParams(parsedUrl.hash.replace(/^#/, ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const code = parsedUrl.searchParams.get("code");

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (!error) {
            navigate("/app", { replace: true });
            return;
          }
        } else if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (!error) {
            navigate("/app", { replace: true });
            return;
          }
        }

        const path = parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        if (path) {
          navigate(path, { replace: true });
        }
      } catch (e) {
        console.error("Deep link parse error:", e);
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [navigate]);
};
